/**
 * SDK de analytics first-party do blog (E3.3).
 *
 * Sem dependência de React de propósito: será chamado de dentro de um
 * `useEffect` na página do post, mais adiante (fora do escopo desta tarefa).
 * Nada de PostHog aqui — PostHog continua só para o app logado
 * (`src/lib/posthogClient.ts`).
 *
 * Tudo aqui é "melhor esforço": qualquer falha (feature ausente, exceção de
 * runtime, rede fora do ar) é engolida silenciosamente. Uma falha de
 * telemetria NUNCA pode quebrar a página do blog — por isso todo bloco de
 * risco está em try/catch e as funções públicas nunca lançam.
 */

import { CTA_LEAD_LINK_SELECTOR } from "./blogCtaLead";

/** Tipos de evento do contrato (`docs/blog/api-contracts.md`, seção 3.1). */
export type BlogAnalyticsEventType =
  | "view"
  | "heartbeat"
  | "scroll_depth"
  | "cta_click"
  | "lead"
  | "copy_block"
  | "calc_used"
  | "share"
  | "internal_link_click"
  | "llm_copy"
  | "llm_markdown_view";

type Utm = {
  source?: string | null;
  medium?: string | null;
  campaign?: string | null;
  term?: string | null;
  content?: string | null;
} | null;

type Device = "mobile" | "tablet" | "desktop";

/**
 * Formato do evento. `session_id` e `visitor_id` NÃO entram aqui de
 * propósito: quem decide os dois é o BACKEND (cookie first-party + hash
 * diário). Mandar esses campos do cliente seria inútil — o servidor ignora —
 * e ruído a mais no payload.
 */
interface OutgoingEvent {
  slug: string;
  post_id?: string;
  type: BlogAnalyticsEventType;
  ts: string;
  meta?: Record<string, unknown>;
  referrer?: string | null;
  utm?: Utm;
  device: Device;
}

export interface StartPostTrackingOptions {
  slug: string;
  postId?: string;
  /**
   * Container do post no DOM, usado para: delegação de clique (CTA, link
   * interno, calculadora), escuta de `copy` e cálculo de profundidade de
   * rolagem. Default: `document.body`.
   */
  container?: HTMLElement | null;
}

/**
 * Mesma origem, sempre. O caminho é um rewrite puro no `vercel.json` que
 * aponta para `v1/blog/analytics/events` no backend — nenhum código e nenhum
 * segredo do backend moram na Vercel.
 *
 * O rewrite existe por dois motivos concretos, e trocar por chamada direta a
 * api.fidexa.com.br quebra os dois: endereço de analytics em outro domínio
 * entra nas listas de bloqueio de rastreamento, e o cookie de sessão viraria
 * cookie de terceiro — que o Safari descarta.
 */
const BEACON_ENDPOINT = "/api/t";
const HEARTBEAT_INTERVAL_MS = 15_000;
// Tick mais granular que o próprio heartbeat: assim, se a aba ficar oculta
// no meio do caminho, só contamos os segundos em que ela esteve visível de
// fato, em vez de contar/perder o intervalo de 15s inteiro no chute.
const ACCUMULATOR_TICK_MS = 1_000;
const FLUSH_INTERVAL_MS = 10_000;
const SCROLL_MILESTONES = [25, 50, 75, 100] as const;
const MAX_QUEUE_SIZE = 200; // trava de segurança contra fila crescendo sem limite.

const detectDevice = (): Device => {
  try {
    const width = window.innerWidth;
    if (width < 640) return "mobile";
    if (width < 1024) return "tablet";
    return "desktop";
  } catch {
    return "desktop";
  }
};

const parseUtmFromLocation = (): Utm => {
  try {
    const params = new URLSearchParams(window.location.search);
    const source = params.get("utm_source");
    const medium = params.get("utm_medium");
    const campaign = params.get("utm_campaign");
    const term = params.get("utm_term");
    const content = params.get("utm_content");
    if (!source && !medium && !campaign && !term && !content) return null;
    return { source, medium, campaign, term, content };
  } catch {
    return null;
  }
};

const nowIso = (): string => new Date().toISOString();

/**
 * Fila + agendadores de um "tracker" ativo. Guardado num closure por chamada
 * de `startPostTracking` — nada de estado global compartilhado entre posts.
 */
const createTracker = (options: StartPostTrackingOptions) => {
  const { slug, postId } = options;
  const container = options.container ?? (typeof document !== "undefined" ? document.body : null);

  const queue: OutgoingEvent[] = [];
  const emittedScrollMilestones = new Set<number>();
  const device = detectDevice();
  const utm = parseUtmFromLocation();
  const referrer = typeof document !== "undefined" ? document.referrer || null : null;

  const enqueue = (type: BlogAnalyticsEventType, meta?: Record<string, unknown>) => {
    try {
      if (queue.length >= MAX_QUEUE_SIZE) return; // melhor perder evento do que crescer sem limite.
      queue.push({
        slug,
        post_id: postId,
        type,
        ts: nowIso(),
        meta,
        referrer,
        utm,
        device,
      });
    } catch {
      // Nunca deixa um erro de telemetria vazar para a página.
    }
  };

  // O servidor (`api/t.ts`) recusa lote acima de 50 eventos, e recusa o lote
  // INTEIRO — responde 204 como se tivesse aceitado. Mandar 51 aqui perderia
  // tudo, dos dois lados, sem sinal nenhum. Fatiar é o que impede essa perda
  // invisível. Manter o número igual ao do servidor.
  const MAX_EVENTS_PER_REQUEST = 50;

  const flush = (useBeacon: boolean) => {
    if (queue.length === 0) return;
    while (queue.length > 0) sendBatch(queue.splice(0, MAX_EVENTS_PER_REQUEST), useBeacon);
  };

  const sendBatch = (batch: OutgoingEvent[], useBeacon: boolean) => {
    try {
      const body = JSON.stringify(batch);

      if (useBeacon) {
        // Envio final no pagehide: sendBeacon é o único jeito confiável de
        // garantir que a última batida saia antes da aba fechar de vez.
        if (typeof navigator === "undefined" || typeof navigator.sendBeacon !== "function") {
          // Inerte quando sendBeacon não existe — não tenta fallback aqui,
          // por design: no momento do pagehide um fetch normal já não tem
          // garantia nenhuma de completar mesmo com keepalive.
          return;
        }
        const blob = new Blob([body], { type: "application/json" });
        navigator.sendBeacon(BEACON_ENDPOINT, blob);
        return;
      }

      if (typeof fetch !== "function") return;
      void fetch(BEACON_ENDPOINT, {
        method: "POST",
        headers: { "content-type": "application/json" },
        body,
        keepalive: true,
      }).catch(() => {
        // Falha de rede na telemetria é só ruído — nunca propaga.
      });
    } catch {
      // Idem: nunca lança.
    }
  };

  // --- heartbeat: soma de "tempo visível", nunca diferença de timestamps ---
  let visibleAccumMs = 0;
  const accumulatorTimer = setInterval(() => {
    try {
      if (typeof document === "undefined" || document.visibilityState !== "visible") return;
      visibleAccumMs += ACCUMULATOR_TICK_MS;
      if (visibleAccumMs >= HEARTBEAT_INTERVAL_MS) {
        visibleAccumMs -= HEARTBEAT_INTERVAL_MS;
        enqueue("heartbeat");
      }
    } catch {
      // no-op
    }
  }, ACCUMULATOR_TICK_MS);

  // --- flush periódico ---
  const flushTimer = setInterval(() => flush(false), FLUSH_INTERVAL_MS);

  // --- profundidade de rolagem ---
  let scrollRaf = 0;
  const computeScrollPct = (): number => {
    try {
      const el = container ?? document.body;
      const rect = el.getBoundingClientRect();
      const total = rect.height || 1;
      const viewed = window.innerHeight - rect.top;
      return Math.max(0, Math.min(100, Math.round((viewed / total) * 100)));
    } catch {
      return 0;
    }
  };

  const onScroll = () => {
    if (scrollRaf) return;
    scrollRaf = window.requestAnimationFrame(() => {
      scrollRaf = 0;
      try {
        const pct = computeScrollPct();
        for (const milestone of SCROLL_MILESTONES) {
          if (pct >= milestone && !emittedScrollMilestones.has(milestone)) {
            emittedScrollMilestones.add(milestone);
            enqueue("scroll_depth", { scroll_pct: milestone });
          }
        }
      } catch {
        // no-op
      }
    });
  };

  // --- cliques delegados no container do post ---
  //
  // Convenção esperada quando as páginas forem ligadas ao SDK:
  // `data-analytics-event="cta_click" | "calc_used" | "share"` em qualquer
  // elemento clicável, com `data-analytics-id` opcional (vira `meta.block_id`).
  // Link interno é detectado automaticamente (não precisa de atributo): todo
  // `<a>` de mesma origem dentro do container conta como `internal_link_click`,
  // a menos que tenha `data-analytics-event="ignore"`.
  const CLICK_EVENT_TYPES = new Set<BlogAnalyticsEventType>(["cta_click", "calc_used", "share"]);

  const onClick = (event: MouseEvent) => {
    try {
      const target = event.target as HTMLElement | null;
      if (!target) return;

      const explicit = target.closest<HTMLElement>("[data-analytics-event]");
      if (explicit) {
        const type = explicit.dataset.analyticsEvent as BlogAnalyticsEventType | "ignore" | undefined;
        if (type && type !== "ignore" && CLICK_EVENT_TYPES.has(type)) {
          const blockId = explicit.dataset.analyticsId;
          enqueue(type, blockId ? { block_id: blockId } : undefined);
          return;
        }
      }

      const anchor = target.closest<HTMLAnchorElement>("a[href]");
      // O botão do bloco `cta-lead` é CTA, não link interno: no navegador ele
      // abre o formulário de demo (`blogCtaLead.ts`) em vez de navegar.
      if (anchor && container?.contains(anchor) && anchor.matches(CTA_LEAD_LINK_SELECTOR)) {
        enqueue("cta_click", { block_id: "cta-lead" });
        return;
      }
      if (anchor && container?.contains(anchor)) {
        const isInternal = anchor.href.startsWith(window.location.origin);
        const isIgnored = anchor.dataset.analyticsEvent === "ignore";
        if (isInternal && !isIgnored) {
          enqueue("internal_link_click", { href: anchor.getAttribute("href") });
        }
      }
    } catch {
      // no-op
    }
  };

  /**
   * Cópia de trecho do post pelo leitor.
   *
   * Emite SEMPRE `copy_block`. Não há como distinguir aqui uma cópia "para
   * IA": rastreador de LLM (GPTBot, ClaudeBot, CCBot…) não executa JavaScript
   * — ele busca o HTML e vai embora, sem nunca disparar evento de clipboard.
   * Classificar por User-Agent neste ponto produziria um evento que jamais
   * dispara, e um `llm_copy: 0` no painel seria lido como "ninguém copia para
   * IA" quando na verdade nada estaria medindo isso.
   *
   * `llm_copy` era a ação HUMANA do botão "Copiar para LLM" (E8.3), removido
   * da página do post em 2026-09-23 — o tipo continua aqui porque o histórico
   * gravado com ele segue válido no painel.
   */
  const onCopy = () => {
    try {
      const selection = typeof window !== "undefined" ? window.getSelection()?.toString() : "";
      enqueue("copy_block", selection ? { chars: selection.length } : undefined);
    } catch {
      // no-op
    }
  };

  const onPageHide = () => flush(true);
  const onVisibilityChange = () => {
    // Ao esconder a aba, aproveita para mandar o que já tem na fila —
    // reduz perda de eventos em mobile, onde `pagehide` nem sempre dispara
    // a tempo. Não usa sendBeacon aqui (não é o desligamento final).
    try {
      if (document.visibilityState === "hidden") flush(false);
    } catch {
      // no-op
    }
  };

  const attach = () => {
    try {
      window.addEventListener("scroll", onScroll, { passive: true });
      window.addEventListener("pagehide", onPageHide);
      document.addEventListener("visibilitychange", onVisibilityChange);
      container?.addEventListener("click", onClick);
      container?.addEventListener("copy", onCopy);
    } catch {
      // no-op
    }
  };

  const detach = () => {
    try {
      clearInterval(accumulatorTimer);
      clearInterval(flushTimer);
      if (scrollRaf) window.cancelAnimationFrame(scrollRaf);
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("pagehide", onPageHide);
      document.removeEventListener("visibilitychange", onVisibilityChange);
      container?.removeEventListener("click", onClick);
      container?.removeEventListener("copy", onCopy);
    } catch {
      // no-op
    }
  };

  return { enqueue, flush, attach, detach };
};

/**
 * Inicia o rastreio de um post. Retorna uma função de parada — chamar no
 * cleanup do `useEffect` da página (quando ligarmos o SDK lá).
 *
 * Dispara `view` imediatamente, agenda `heartbeat` (só com aba visível),
 * observa profundidade de rolagem e delega clique/cópia no container.
 */
export const startPostTracking = (options: StartPostTrackingOptions): (() => void) => {
  try {
    if (typeof window === "undefined" || typeof document === "undefined") {
      return () => {};
    }
    if (!options.slug) return () => {};

    const tracker = createTracker(options);
    tracker.attach();
    tracker.enqueue("view");

    let stopped = false;
    return () => {
      if (stopped) return;
      stopped = true;
      try {
        tracker.detach();
        // Última chance de esvaziar a fila ao desmontar (ex.: navegação
        // client-side entre posts, que não passa por `pagehide`).
        tracker.flush(false);
      } catch {
        // no-op
      }
    };
  } catch {
    // Nunca deixa o SDK quebrar a página que o chamou.
    return () => {};
  }
};

/**
 * Disparo manual de evento, para casos que não vêm de clique/scroll dentro
 * do container observado por `startPostTracking` — ex.: `lead` (submit de
 * formulário em outro componente) ou `llm_markdown_view` (acesso à rota
 * alternativa em Markdown, fora do fluxo de página React). Fica pronto para
 * ser chamado pelas páginas quando a ligação acontecer.
 */
export const trackBlogEvent = (
  slug: string,
  type: BlogAnalyticsEventType,
  meta?: Record<string, unknown>,
  postId?: string
): void => {
  try {
    if (typeof window === "undefined" || typeof document === "undefined") return;
    if (!slug) return;

    const body = JSON.stringify([
      {
        slug,
        post_id: postId,
        type,
        ts: nowIso(),
        meta,
        referrer: document.referrer || null,
        utm: parseUtmFromLocation(),
        device: detectDevice(),
      } satisfies OutgoingEvent,
    ]);

    if (typeof fetch === "function") {
      void fetch(BEACON_ENDPOINT, {
        method: "POST",
        headers: { "content-type": "application/json" },
        body,
        keepalive: true,
      }).catch(() => {
        // no-op
      });
    }
  } catch {
    // no-op
  }
};
